import React, { useState, useEffect } from "react"; 
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo"; 
import SearchBar from "../components/SearchBar";

// 🟢 NEW: Haptic Feedback Engine
const vibrate = (pattern) => {
  if (typeof window !== "undefined" && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
};

function Navbar({ cartCount }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State for Mobile Menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null); 

  // Sync User State
  useEffect(() => {
    const userString = localStorage.getItem("user");
    try {
      setUser(userString ? JSON.parse(userString) : null);
    } catch (error) {
      setUser(null);
    }
  }, [location]); 

  // Helper to check active link
  const isActive = (path) => location.pathname === path;
  
  // Close menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Prevent scrolling when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);

  const handleLogout = () => {
    vibrate(20); // 📳 Micro tap when clicking logout
    if(window.confirm("Are you sure you want to log out?")) {
      vibrate([50, 50, 50]); // 📳 Success burst on confirm
      localStorage.removeItem("userToken");
      localStorage.removeItem("user");
      setUser(null); 
      navigate("/");
    }
  };

  // Smart Cart Icon Handler
  const handleSmartCartClick = (e) => {
    e.preventDefault(); 

    if (!cartCount || cartCount === 0) {
      vibrate([100, 50, 100]); // 📳 Error double-tap if empty
      alert("Your cart is empty! 🛒"); 
      return;
    }

    vibrate(50); // 📳 Firm tap for opening cart

    // If on the Shop Page, trigger the Drawer to open
    if (location.pathname === "/") {
      window.dispatchEvent(new Event('openCartDrawer'));
    } else {
      // If on any other page, navigate directly to Checkout
      navigate("/checkout");
    }
  };

  // 🟢 NEW: Home Icon Refresher
  // When clicked, if they are already on "/", force a reload to shuffle products!
  const handleHomeClick = (e) => {
    vibrate(20); // 📳 Micro tap
    if (location.pathname === "/") {
      e.preventDefault();
      window.location.reload(); 
    }
  };

  const handleMenuToggle = () => {
    vibrate(20); // 📳 Micro tap for opening/closing side menu
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Animated Link Component (Desktop)
  const NavLink = ({ to, label }) => (
    <Link to={to} onClick={() => vibrate(20)} className={`relative group py-1 ${isActive(to) ? "text-green-600 font-bold" : "text-gray-600 hover:text-green-600"} transition-colors duration-300`}>
      {label}
      <span className={`absolute bottom-0 left-0 h-[2px] bg-green-500 transition-all duration-300 ease-out ${isActive(to) ? "w-full" : "w-0 group-hover:w-full"}`}></span>
    </Link>
  );

  return (
    <>
      {/* ============================== */}
      {/* 🖥️ DESKTOP NAVBAR */}
      {/* ============================== */}
      <nav className="hidden md:flex sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 px-6 py-4 justify-between items-center shadow-sm transition-all h-[80px]">
        {/* Logo */}
        <Link to="/" onClick={handleHomeClick} className="hover:scale-105 transition-transform duration-300 flex items-center shrink-0">
          <Logo className="h-10 w-auto" /> 
        </Link>

        {/* Desktop Search Bar (Advanced) */}
        <div className="flex-1 max-w-lg mx-8 relative z-50">
           <SearchBar />
        </div>

        <div className="flex items-center gap-6 font-semibold text-gray-600 shrink-0">
          <NavLink to="/" label="Home" />
          <NavLink to="/my-orders" label="My Orders" />
          
          {/* DESKTOP CART LINK (Smart Logic) */}
          <button 
            onClick={handleSmartCartClick}
            className="relative bg-green-50 text-green-700 px-4 py-2 rounded-full hover:bg-green-100 hover:shadow-sm transition-all duration-300 flex items-center gap-2 border border-green-200 active:scale-95 cursor-pointer"
          >
            <span className="text-lg">🛒</span>
            <span className="text-sm">Cart</span>
            {(cartCount > 0) && (
              <span className="bg-green-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shadow-sm animate-bounce">
                {cartCount}
              </span>
            )}
          </button>

          {user ? (
            <div className="flex items-center gap-4 pl-4 border-l border-gray-300 animate-fade-in">
              <div className="flex flex-col items-end">
                <span className="text-[10px] text-gray-400 font-medium uppercase">Welcome</span>
                <div className="flex items-center gap-1">
                  <span className="text-lg animate-wave origin-bottom-right inline-block">👋</span>
                  <span className="text-sm font-bold text-gray-800">{user.name?.split(" ")[0]}</span>
                </div>
              </div>
              <button onClick={handleLogout} className="text-red-500 hover:text-red-700 text-sm font-bold hover:scale-105 transition-transform">Logout</button>
            </div>
          ) : (
            <Link to="/login" onClick={() => vibrate(20)} className="px-6 py-2 bg-green-600 text-white rounded-full text-sm font-bold shadow-md hover:bg-green-700 hover:shadow-lg transition-all active:scale-95">Login</Link>
          )}
        </div>
      </nav>

      {/* ============================== */}
      {/* 📱 MOBILE HEADER (Fixed Top) */}
      {/* ============================== */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-sm transition-all duration-300">
        <div className="flex justify-between items-center px-4 py-2 border-b border-gray-50 h-[60px]">
          <Link to="/" onClick={(e) => { setIsMobileMenuOpen(false); handleHomeClick(e); }}>
             <Logo className="h-7 w-auto active:scale-95 transition-transform" />
          </Link>
          
          <button 
            onClick={handleMenuToggle} 
            className="p-2 text-gray-700 focus:outline-none transition-transform duration-300 active:scale-90"
          >
            <span className="text-2xl font-bold block">☰</span>
          </button>
        </div>

        {/* MOBILE SEARCH BAR (Advanced) */}
        <div className="px-4 py-2 bg-white pb-3 relative z-40">
           <SearchBar />
        </div>
      </div>

      <div className="md:hidden h-[110px] w-full bg-gray-50"></div>

      {/* ============================== */}
      {/* 💎 GLASS MENU OVERLAY */}
      {/* ============================== */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[100] bg-black/40 backdrop-blur-md flex items-center justify-center animate-in fade-in duration-300">
          <div className="absolute inset-0" onClick={handleMenuToggle}></div>
          
          <div className="relative w-10/12 max-w-sm bg-white/95 backdrop-blur-xl border border-white/50 shadow-2xl rounded-2xl p-6 transform transition-all animate-in zoom-in-95 duration-300">
            <button 
              onClick={handleMenuToggle} 
              className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors p-2"
            >
              <span className="text-xl font-bold">✕</span>
            </button>
            <div className="flex flex-col space-y-4 pt-4">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest text-center mb-2">Menu</h3>
              <Link to="/about" onClick={handleMenuToggle} className="flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-xl hover:bg-green-50 font-bold text-gray-700">
                <span className="text-xl">📖</span> About Us
              </Link>
              <Link to="/contact" onClick={handleMenuToggle} className="flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-xl hover:bg-green-50 font-bold text-gray-700">
                <span className="text-xl">📞</span> Contact Support
              </Link>
              <Link to="/my-orders" onClick={handleMenuToggle} className="flex items-center gap-4 px-4 py-3 bg-gray-50 rounded-xl hover:bg-green-50 font-bold text-gray-700">
                <span className="text-xl">📦</span> My Orders
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* ============================== */}
      {/* 📱 MOBILE BOTTOM BAR */}
      {/* ============================== */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 z-[90] pb-safe safe-area-inset-bottom shadow-[0_-5px_20px_rgba(0,0,0,0.03)]">
        <div className="flex justify-around items-center h-16 relative">
          
          {/* 🟢 FIXED: Home Icon handles reload to refresh products */}
          <Link to="/" onClick={handleHomeClick} className="flex flex-col items-center gap-1 w-full h-full justify-center active:scale-90 transition-transform duration-200">
            <span className={`text-2xl ${isActive("/") ? "grayscale-0 scale-110" : "grayscale opacity-40"} transition-all duration-300`}>🏠</span>
            <span className={`text-[10px] font-bold ${isActive("/") ? "text-green-600" : "text-gray-400"}`}>Home</span>
          </Link>

          <Link to="/my-orders" onClick={() => vibrate(20)} className="flex flex-col items-center gap-1 w-full h-full justify-center active:scale-90 transition-transform duration-200">
            <span className={`text-2xl ${isActive("/my-orders") ? "grayscale-0 scale-110" : "grayscale opacity-40"} transition-all duration-300`}>📦</span>
            <span className={`text-[10px] font-bold ${isActive("/my-orders") ? "text-green-600" : "text-gray-400"}`}>Orders</span>
          </Link>

          {/* Floating Cart */}
          <div className="relative -top-6">
            <button 
              onClick={handleSmartCartClick}
              className="bg-green-600 w-14 h-14 rounded-full flex items-center justify-center text-white shadow-xl shadow-green-200 border-[5px] border-white active:scale-90 hover:-translate-y-1 transition-all duration-300"
            >
              <span className="text-xl">🛒</span>
              {(cartCount > 0) && (
                <span className="absolute top-0 right-0 bg-red-500 text-white text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-white animate-pulse">
                  {cartCount}
                </span>
              )}
            </button>
          </div>

          {user ? (
            <div onClick={handleLogout} className="flex flex-col items-center gap-1 w-full h-full justify-center active:scale-90 transition-transform duration-200 cursor-pointer">
              <span className="text-2xl animate-wave origin-bottom-right">👋</span>
              <span className="text-[10px] font-bold text-green-600">Hi, {user.name?.split(" ")[0]}</span>
            </div>
          ) : (
            <Link to="/login" onClick={() => vibrate(20)} className="flex flex-col items-center gap-1 w-full h-full justify-center active:scale-90 transition-transform duration-200">
              <span className={`text-2xl ${isActive("/login") ? "grayscale-0 scale-110" : "grayscale opacity-40"} transition-all duration-300`}>👤</span>
              <span className={`text-[10px] font-bold ${isActive("/login") ? "text-green-600" : "text-gray-400"}`}>Login</span>
            </Link>
          )}
        </div>
      </nav>

      {/* CSS ANIMATION STYLE */}
      <style>{`
        @keyframes wave {
          0% { transform: rotate(0deg); }
          10% { transform: rotate(14deg); }
          20% { transform: rotate(-8deg); }
          30% { transform: rotate(14deg); }
          40% { transform: rotate(-4deg); }
          50% { transform: rotate(10deg); }
          60% { transform: rotate(0deg); }
          100% { transform: rotate(0deg); }
        }
        .animate-wave {
          animation: wave 2s ease-in-out 1;
        }
      `}</style>
    </>
  );
}

export default Navbar;